import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MamaApprovedHero from './components/MamaApprovedHero';
import BusinessCard from './components/BusinessCard';
import BusinessDetailModal from './components/BusinessDetailModal';
import { mamaApprovedBusinesses, categoryFilters, MamaApprovedBusiness } from '../../mocks/mamaApproved';
import SEO from '../../components/feature/SEO';

export default function MamaApprovedPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedBusiness, setSelectedBusiness] = useState<MamaApprovedBusiness | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  const filtered = mamaApprovedBusinesses.filter((b) => {
    const matchesCategory = activeCategory === 'All' || b.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      b.name.toLowerCase().includes(q) ||
      b.description.toLowerCase().includes(q) ||
      b.category.toLowerCase().includes(q) ||
      b.tags.some((t) => t.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Mama Approved Businesses | Trusted by Gwinnett Moms',
    description:
      'Businesses vetted and recommended by local moms in The Mommy Mingle community across Gwinnett County and surrounding areas.',
    url: `${siteUrl}/mama-approved`,
    itemListOrder: 'https://schema.org/ItemListUnordered',
    numberOfItems: 150,
    publisher: {
      '@type': 'Organization',
      name: 'The Mommy Mingle',
      url: siteUrl,
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Mama Approved Businesses | Trusted by Gwinnett Moms | The Mommy Mingle"
        description="Shop and hire from businesses trusted by local moms in Gwinnett County. Every listing is vetted and recommended by real moms in The Mommy Mingle community."
        keywords="mama approved businesses Gwinnett, trusted local businesses Georgia, mom recommended services Gwinnett County"
        canonical="/mama-approved"
        schema={schema}
      />
      <Navbar />

      <MamaApprovedHero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        categories={categoryFilters}
        totalCount={filtered.length}
      />

      {/* Grid Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
                <i className="ri-medal-fill text-amber-500 text-xs"></i>
                Trusted Vendor Directory
              </div>
              <h2 className="text-xl font-extrabold text-gray-900">
                {activeCategory === 'All' ? 'All Mama Approved Businesses' : activeCategory}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Vetted &amp; recommended by local moms in Gwinnett County &mdash; trusted by local moms and businesses across Gwinnett and surrounding areas
              </p>
            </div>
            <a
              href="/partner"
              className="flex items-center gap-1.5 px-4 py-2 bg-rose-50 border border-rose-200 text-rose-600 text-xs font-semibold rounded-full hover:bg-rose-100 transition-colors whitespace-nowrap cursor-pointer shrink-0"
            >
              <i className="ri-handshake-line text-xs"></i>
              Want to be featured? Apply to partner
            </a>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((business) => (
                <BusinessCard
                  key={business.id}
                  business={business}
                  onClick={setSelectedBusiness}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">No businesses found</h3>
              <p className="text-sm text-gray-400">Try adjusting your search or filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-16 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-400 rounded-full mb-5 shadow-md">
            <i className="ri-medal-fill text-gray-900 text-2xl"></i>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            What Does &ldquo;Mama Approved&rdquo; Mean?
          </h2>
          <p className="text-gray-600 text-base leading-relaxed max-w-2xl mx-auto mb-8">
            Every business on this page has been personally recommended and reviewed by moms in The Mommy Mingle community. We don&apos;t just list anyone — these are businesses that local moms in Sugar Hill, Gwinnett County, and surrounding areas have tried, trusted, and love.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left mb-10">
            {[
              { icon: 'ri-user-heart-line', title: 'Mom Vetted', desc: 'Real reviews from real moms in our community — no fake ratings, no paid placements.' },
              { icon: 'ri-shield-check-line', title: 'Community Trusted', desc: 'Businesses that consistently earn top marks from Gwinnett County families.' },
              { icon: 'ri-gift-2-line', title: 'Exclusive Discounts', desc: 'Every Mama Approved business offers a special deal exclusively for Mommy Mingle members.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-pink-100 rounded-xl mb-3">
                  <i className={`${item.icon} text-pink-600 text-lg`}></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900 mb-1.5">{item.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Apply to partner CTA */}
          <div className="bg-white rounded-2xl border border-gray-200 px-8 py-6 max-w-xl mx-auto">
            <h3 className="text-base font-extrabold text-gray-900 mb-1">Want to be featured here?</h3>
            <p className="text-sm text-gray-500 mb-4">
              Businesses can apply to be featured or partner with The Mommy Mingle.
            </p>
            <a
              href="/partner"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-rose-500 text-white text-sm font-bold rounded-full hover:bg-rose-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-handshake-line"></i>
              Apply to Partner
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {selectedBusiness && (
        <BusinessDetailModal
          business={selectedBusiness}
          onClose={() => setSelectedBusiness(null)}
        />
      )}
    </div>
  );
}
